// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Check, Copy } from 'lucide-react';

import { copyTextToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';

export interface CodeBlockProps {
  caption: string;
  code: string;
  /** Names what this button copies when several sit on one card. */
  copyLabel?: string;
  className?: string;
}

export function CodeBlock({
  caption,
  code,
  copyLabel,
  className,
}: CodeBlockProps): React.ReactElement {
  const [copied, setCopied] = React.useState(false);
  // Mirror the timer-cleanup pattern from MonoValue so the reset timer can't
  // fire after unmount or be clobbered by a rapid second click.
  const resetTimerRef = React.useRef<number | null>(null);

  const clearResetTimer = React.useCallback(() => {
    if (resetTimerRef.current !== null) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => clearResetTimer, [clearResetTimer]);

  const handleCopy = React.useCallback(async () => {
    if (!(await copyTextToClipboard(code))) return;
    setCopied(true);
    clearResetTimer();
    resetTimerRef.current = window.setTimeout(() => {
      resetTimerRef.current = null;
      setCopied(false);
    }, 1600);
  }, [code, clearResetTimer]);

  return (
    <div className={cn('rounded-gl overflow-hidden border border-border bg-muted/40', className)}>
      {/* Caption bar */}
      <div className="flex h-9 items-center justify-between border-b border-border bg-muted/40 px-3.5">
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          {caption}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : (copyLabel ?? 'Copy code')}
          className="inline-flex min-h-[44px] md:min-h-0 items-center gap-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              Copy
            </>
          )}
        </button>
        <span className="sr-only" aria-live="polite">
          {copied ? 'Copied to clipboard' : ''}
        </span>
      </div>

      {/* Code body */}
      <pre className="overflow-x-auto whitespace-pre-wrap wrap-anywhere p-3.5 font-mono text-[12px] leading-[1.7] text-foreground">
        {code}
      </pre>
    </div>
  );
}
